<template>
  <div class="o-ring-design-cockpit__container">
    <div class="o-ring-design-cockpit__content">
      <div class="o-ring-design-cockpit__content__tabs">
        <TabStrip
          v-if="tabs && tabs.length > 0"
          :tabs="tabs"
          class="tabstrip"
          :selected="selected"
          @select="onSelect"
        >
          <template #construction>
            <ORingAssistantDetailsDesign :show-headers="false" />
          </template>
          <template #design>
            <div class="catalog-ring">
              <h4>{{ i18n.tv('results', 'ORingConfigurator') }}</h4>
            </div>
            <span
              v-if="resultsCount > 0"
              class="catalog-ring-description"
            >
              {{ i18n.tv('Frontend.Wizard.OnlyIndustry', 'ORingConfiguratorImport') }}
            </span>
            <ORingResultsGrid
              v-if="resultsCount > 0"
              :table-columns="tableColumns"
              :data-items="mappedTableData"
              :is-inch-computed="oringStore.oringCalculatorImperial ? 'inch' : 'mm'"
              :is-design-cockpit="true"
              class="oring-results-grid"
            />
            <div
              v-if="resultsCount === 0"
              class="no-products"
            >
              <NoProductsToShow
                class="show_no_products_icon"
              />
              <span>{{ i18n.tv('noResults', 'ORingConfigurator') }}</span>
            </div>
          </template>
        </TabStrip>
      </div>
    </div>
    <LeaveConfirmationPopup
      :show="showPopup"
      :size="POPUP.Size.small"
      :title="i18n.tv('Frontend.Wizard.Note', 'ORingConfiguratorImport')"
      :content="i18n.tv('Frontend.Wizard.LeaveDescription', 'ORingConfiguratorImport')"
      :stay-button-text="i18n.tv('Frontend.Wizard.Stay', 'ORingConfiguratorImport')"
      :leave-button-text="i18n.tv('Frontend.Wizard.Leave', 'ORingConfiguratorImport')"
      @update:show="showPopup = $event"
      @leave="closeORingAssistant"
    />
  </div>
</template>
<script setup lang="ts">
import useI18nExtended from '~/composables/usei18nExtended'
import {POPUP} from '~/assets/js/constants'
import LeaveConfirmationPopup from '~/components/popup/LeaveConfirmationPopup.vue'
import {useORingCalculatorStore} from '@/store/oring-calculator'
import {TabStrip} from '@progress/kendo-vue-layout'
import ORingAssistantDetailsDesign from '~/components/oring-calculator/ORingAssistantDetailsDesign.vue'
import ORingResultsGrid from '~/components/oring-calculator/ORingResultsGrid.vue'
import {useOringCalculatorService} from '~/composables/oring_calc_service'
import NoProductsToShow from '~/assets/icons/no_products_found.svg?component'

const {calculateResultExpertMode} = useOringCalculatorService()

const i18n = useI18nExtended()
const app = useNuxtApp()
const showPopup = ref(false)
const oringStore = useORingCalculatorStore()
const response = ref<any>(null)

const {oringDesignCockpitCalculateResult} = storeToRefs(oringStore)

const tableColumns = computed(() => {
  const baseColumns = [
    {field: 'articleNo', title: i18n.tv('itemNo', 'cart'), width: 138, cell: 'sku', className: 'cell-with-icon'},
    {field: 'isAvailable', title: i18n.tv('Frontend.Parameter.Availability', 'ORingConfiguratorImport'), width: 138, cell: 'availability', className: 'mobile--visible'},
    {field: 'materialName', title: i18n.tv('Frontend.MaterialAssistant.Material', 'ORingConfiguratorImport'), width: 138, cell: 'simpleCell'},
    {field: 'd1', title: i18n.tv('Attribute.d1', 'ORingConfiguratorImport'), width: 100, cell: 'dimensionCell', headerClassName: 'right-align-header'},
    {field: 'd2', title: i18n.tv('Attribute.d2', 'ORingConfiguratorImport'), width: 100, cell: 'dimensionCell', headerClassName: 'right-align-header'}
  ]

  const geometryId = oringStore.oringDesignCockpitGeometryId
  let conditionalColumns: any = []

  if (geometryId === 9 || geometryId === 12) {
    conditionalColumns.push({field: 'stretching', title: i18n.tv('stretching', 'ORingConfigurator'), width: geometryId === 12 ? 74 : 138, cell: 'stretching', minField: geometryId === 12 ? null : 'stretchingMin', maxField: 'stretchingMax', headerClassName: 'right-align-header'})
  }

  if (geometryId === 8 || geometryId === 12) {
    conditionalColumns.push({field: 'holdingRatio', title: i18n.tv('holdingRatio', 'ORingConfigurator'), width: geometryId === 12 ? 74 : 138, cell: 'stretching', minField: geometryId === 12 ? null : 'holdingRatioMin', maxField: 'holdingRatioMax', headerClassName: 'right-align-header'})
  }

  const compressionColumn = {field: 'compression', title: i18n.tv('centricCompression', 'ORingConfigurator'), width: 156, cell: 'stretching', minField: 'compressionMin', maxField: 'compressionMax', meanField: 'compressionMean', headerClassName: 'right-align-header'}
  const fillingRatioColumn = {field: 'fillingRatio', title: i18n.tv('Frontend.Parameter.FillingRatio', 'ORingConfiguratorImport'), width: 156, cell: 'stretching', minField: 'fillingRatioMin', maxField: 'fillingRatioMax', meanField: 'fillingRatioMean', headerClassName: 'right-align-header'}
  const endColumns = [
    {field: '', title: i18n.tv('detailsPage', 'ORingConfigurator'), width: 120, cell: 'detailsPage'}
  ]
  return [...baseColumns, ...conditionalColumns, compressionColumn, fillingRatioColumn, ...endColumns]
})

interface Tab {
  title: string
  content: string
}

const tabs: Tab[] = []
tabs.push({
  title: i18n.tv('Frontend.Title.Result.ExpertMode', 'ORingConfiguratorImport'),
  content: 'construction'
})

tabs.push({
  title: i18n.tv('Frontend.ExpertMode.ShowMatchingCatalogRing', 'ORingConfiguratorImport'),
  content: 'design'
})
const mappedTableData = computed(() => {
  return oringDesignCockpitCalculateResult.value?.resultRings?.map((item: any) => {
    const rawMaterial = item.catalogueRing?.materialName || item.oRing?.materialName
    return {
      articleNo: item.catalogueRing?.articleNo || item.oRing?.articleNo,
      productDescription: item.catalogueRing?.productDescription,
      isAvailable: item.catalogueRing?.isAvailable,
      materialName: Array.isArray(rawMaterial) ? rawMaterial[0] : rawMaterial,
      d1: (item.catalogueRing?.d1?.val || item.oRing?.d1?.val)?.toFixed(2),
      d2: (item.catalogueRing?.d2?.val || item.oRing?.d2?.val)?.toFixed(2),
      stretchingMin: item.stretching?.valueMin?.toFixed(1),
      stretchingMax: item.stretching?.valueMax?.toFixed(1),
      stretchingMean: item.stretching?.valueMean?.toFixed(1),
      stretchingMinColorRating: item.stretching?.minColorRating || 0,
      stretchingMaxColorRating: item.stretching?.maxColorRating || 0,
      stretchingMeanColorRating: item.stretching?.meanColorRating || 0,
      holdingRatioMin: item.holdingRatio?.valueMin?.toFixed(1),
      holdingRatioMax: item.holdingRatio?.valueMax?.toFixed(1),
      holdingRatioMean: item.holdingRatio?.valueMean?.toFixed(1),
      holdingRatioMinColorRating: item.holdingRatio?.minColorRating || 0,
      holdingRatioMaxColorRating: item.holdingRatio?.maxColorRating || 0,
      holdingRatioMeanColorRating: item.holdingRatio?.meanColorRating || 0,
      compressionMin: item.compression?.valueMin?.toFixed(1),
      compressionMean: item.compression?.valueMean?.toFixed(1),
      compressionMax: item.compression?.valueMax?.toFixed(1),
      compressionMinColorRating: item.compression?.minColorRating || 0,
      compressionMaxColorRating: item.compression?.maxColorRating || 0,
      compressionMeanColorRating: item.compression?.meanColorRating || 0,
      fillingRatioMin: item.fillingRatio?.valueMin?.toFixed(1),
      fillingRatioMean: item.fillingRatio?.valueMean?.toFixed(1),
      fillingRatioMax: item.fillingRatio?.valueMax?.toFixed(1),
      fillingRatioMinColorRating: item.fillingRatio?.minColorRating || 0,
      fillingRatioMaxColorRating: item.fillingRatio?.maxColorRating || 0,
      fillingRatioMeanColorRating: item.fillingRatio?.meanColorRating || 0
    }}) || []
})

const resultsCount = computed(() => {
  return oringDesignCockpitCalculateResult.value?.resultRings?.length || 0
})

onMounted(async () => {
  try {
    oringDesignCockpitCalculateResult.value = await calculateResultExpertMode(oringStore.oringDesignCockpitCalculateResultRequest())
    if (oringDesignCockpitCalculateResult.value?.resultHousing) {
      oringStore.oringDesignCockpitCurrentHousing = oringDesignCockpitCalculateResult.value.resultHousing
    }
  } catch (error) {
    response.value = {
      resultHousing: null,
      resultRings: []
    }
  }
})

const selected: Ref<number> = ref(0)
const onSelect = (e: any) => {
  selected.value = e.selected
}

const closeORingAssistant = () => {
  navigateTo(app.$globalization.localePath('/oring-calculator'))
}
</script>
<style lang="scss">

.o-ring-design-cockpit__container {
  padding-bottom: 1rem;

.o-ring-design-cockpit__content__details {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.o-ring-design-cockpit__content__tabs {
  margin-top: 1rem;

  li {
    width: 15.5rem;

    @include breakpoint-down($sm) {
      width: 50%;
    }

    span {
      justify-content: center;
    }
  }

     .catalog-ring {
      h4 {
        margin: 0 0 1rem;
        font-weight: 600;
      }
    }

    .catalog-ring-description {
      font-weight: 300;
      color:$color__fst__primary;
    }

    .oring-results-grid {
      margin-top: .5rem;
    }

  .k-animation-container {
    width: 100%;
    padding: 1rem;
    margin: -1rem;
  }

  .k-tabstrip-items .k-link {
    padding: .75rem 0 1rem;
  }

  .k-tabstrip.tabstrip .k-tabstrip-items {
    li {
      @include breakpoint-down($sm) {
        width: 50%;
    }
    }
  }

  /* Only apply (or allow) hover visuals on devices that truly support hover */
  @media (hover: hover) and (pointer: fine) {
  /* Intentionally left blank: rely on default Kendo hover styles */
  }

  /* On touch (no real hover) neutralize sticky hover/k-hover styles */
  @media (hover: none) {
    .k-tabstrip.tabstrip .k-tabstrip-items li.k-hover .k-link,
    .k-tabstrip.tabstrip .k-tabstrip-items li .k-link:hover {
      background: $color__white !important;
      color: $color__primary !important;
      box-shadow: none !important;
    }
  }

  /* Keep accessible focus indication for keyboard users */
  .k-tabstrip.tabstrip .k-tabstrip-items li .k-link:focus-visible {
    outline: .125rem solid $color__fst__primary;
    outline-offset: .125rem;
  }

  .no-products {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
    flex-direction: column;

    .show_no_products_icon {
      width: 6.875rem;
      height: 6.875rem;
    }

    span {
      font-weight: $base-text-font-weight-semi-bold;
      color: $color__fst__black;
    }
  }

  @include breakpoint-down($sm) {
    .oring-results-grid-wrapper .k-grid table tr {
      width: calc(100vw - 4.5rem);
      margin-left: 2.2rem;
    }
  }
}
}

 @include breakpoint-down($xl) {
    .oring-results-card {
      overflow-x: auto;
    }

    .k-grid {
      min-width: 80rem;
    }
  }
</style>
